/**
 * Componentes partilhados pelos dashboards de cada perfil.
 *
 * Cada perfil (admin, comercial, engenharia, financeiro, logística) tem o seu
 * próprio sub-componente em `pages/dashboard/`, mas todos consomem estes
 * blocos visuais para garantir um look-and-feel consistente:
 *
 *   - PeriodPicker      — filtro de horizonte (top do dashboard)
 *   - KpiCard           — card grande com valor, ícone, delta e descrição
 *   - DashSectionTitle  — separador discreto entre faixas de cards
 *   - DonutChartCard    — gráfico circular com legenda
 *   - TimeSeriesCard    — gráfico de linhas / barras temporais
 *   - HorizontalBarCard — gráfico de barras (ex: PnL por comercial)
 *   - OperationalTable  — tabela de "RFQs a precisar de acção"
 *
 * Os helpers de cores e formatação ficam aqui também — concentrados num só
 * sítio para que os 5 dashboards usem a mesma paleta sem duplicação.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, Trophy, XCircle, Activity,
  Clock, Layers, Repeat, Users, Calendar, ArrowRight, HelpCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusBadgeVariant } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatEuro, formatNumber } from '@/lib/utils';
import { toIsoDate, formatRangeLabel, isPeriodRangeInvalid } from '@/lib/period';
import { t } from '@/i18n/translations';

/* -------------------------------------------------------------------------- */
/* Paletas — alinhadas com a referência (nflow-style) e com o tema da app    */
/* -------------------------------------------------------------------------- */

// Paleta por categoria — usada nos donuts. Oito cores que se distinguem bem
// em modo claro (a app só tem modo claro hoje) e que se mantêm legíveis em
// fatias pequenas.
export const CHART_PALETTE = [
  '#3B82F6', // azul
  '#10B981', // verde
  '#F59E0B', // âmbar
  '#EF4444', // vermelho
  '#8B5CF6', // violeta
  '#06B6D4', // turquesa
  '#EC4899', // rosa
  '#64748B', // cinzento ardósia
];

// Paleta semântica para os 4 verdicts do PnL audit. Usada nas barras
// empilhadas "PnL por comercial" — cores escolhidas para serem lidas como
// "verde = bom, vermelho = mau" sem precisar de ler a legenda.
export const PNL_VERDICT_COLORS = {
  OK: '#10B981',
  SOBREPRECIFICADO: '#3B82F6',
  SUBPRECIFICADO: '#F59E0B',
  CRITICO: '#EF4444',
};

const ICONS = {
  trending_up: TrendingUp,
  trending_down: TrendingDown,
  trophy: Trophy,
  alert: AlertTriangle,
  layers: Layers,
  activity: Activity,
  clock: Clock,
  repeat: Repeat,
  users: Users,
  calendar: Calendar,
  x: XCircle,
};

const TONE_BG = {
  default: 'bg-muted',
  primary: 'bg-primary/10',
  success: 'bg-success/10',
  destructive: 'bg-destructive/10',
  warning: 'bg-warning/10',
};

const TONE_FG = {
  default: 'text-muted-foreground',
  primary: 'text-primary',
  success: 'text-success',
  destructive: 'text-destructive',
  warning: 'text-warning',
};

/* -------------------------------------------------------------------------- */
/* Formatação                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Tradutor "best-effort" para labels que vêm dos charts.
 *
 * Recebe o `name` exposto à Recharts (`in_analysis`, `loss_reason_price`,
 * `cost_vm`, ``incoterm_ddp``, etc.) e tenta traduzi-lo via i18n. Se a chave
 * não existir, faz humanização defensiva (`in_analysis` → `In analysis`)
 * para evitar mostrar tokens crus ao utilizador. O fallback funciona mesmo
 * se um novo donut for adicionado e a chave i18n esquecida.
 */
export function translateLabel(key, lang) {
  if (key === null || key === undefined) return '—';
  const raw = String(key);
  const translated = t(raw, lang);
  if (translated && translated !== raw) return translated;
  // Humanização — só usada como rede de segurança.
  return raw
    .replace(/^(loss_reason_|incoterm_|cost_|subcontract_|client_type_|pnl_verdict_)/, '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function formatKpiValue(value, format, lang) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  switch (format) {
    case 'euro': return formatEuro(value, lang);
    case 'percent': return `${formatNumber(value, lang, 1)}%`;
    case 'days': return `${formatNumber(value, lang, 1)} d`;
    case 'number': return formatNumber(value, lang);
    default: return String(value);
  }
}

/* -------------------------------------------------------------------------- */
/* PeriodPicker                                                               */
/* -------------------------------------------------------------------------- */

const PERIOD_OPTIONS = [
  { id: 'last_7d',  labelKey: 'period_last_7d' },
  { id: 'last_30d', labelKey: 'period_last_30d' },
  { id: 'last_90d', labelKey: 'period_last_90d' },
  { id: 'ytd',      labelKey: 'period_ytd' },
  { id: 'last_12m', labelKey: 'period_last_12m' },
];

/**
 * Filtro de período do dashboard.
 *
 * ``value`` aceita string (preset) OU objecto ``{ id, from, to }`` (custom).
 * ``onChange`` recebe sempre uma string preset OU um objecto custom — assim
 * o pai pode persistir directamente o período activo.
 */
export function PeriodPicker({ value, onChange, lang }) {
  const isObject = value && typeof value === 'object';
  const activeId = isObject ? value.id : value;
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(isObject ? toIsoDate(value.from) : '');
  const [draftTo, setDraftTo] = useState(isObject ? toIsoDate(value.to) : '');
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isObject) {
      setDraftFrom(toIsoDate(value.from));
      setDraftTo(toIsoDate(value.to));
      return;
    }
    if (!draftFrom || !draftTo) {
      const today = new Date();
      const before = new Date();
      before.setDate(today.getDate() - 30);
      setDraftFrom(toIsoDate(before));
      setDraftTo(toIsoDate(today));
    }
  }, [open, isObject, value, draftFrom, draftTo]);

  const invalid = isPeriodRangeInvalid(draftFrom, draftTo);

  const apply = () => {
    if (!draftFrom || !draftTo || invalid) return;
    onChange({ id: 'custom', from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const customLabel = activeId === 'custom'
    ? formatRangeLabel(value?.from, value?.to, lang)
    : t('period_custom', lang);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-border bg-card p-1 text-xs font-medium shadow-elev-1">
        <Calendar className="ml-2 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'rounded-md px-3 py-1.5 transition-colors',
              activeId === opt.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            )}
          >
            {t(opt.labelKey, lang)}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'rounded-md px-3 py-1.5 transition-colors',
            activeId === 'custom'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted'
          )}
        >
          {customLabel}
        </button>
      </div>
      {open ? (
        <div className="absolute right-0 z-30 mt-2 w-[320px] rounded-lg border border-border bg-card p-3 shadow-elev-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
              <span>{t('period_from', lang)}</span>
              <input
                type="date"
                value={draftFrom}
                max={draftTo || undefined}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
              <span>{t('period_to', lang)}</span>
              <input
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                max={toIsoDate(new Date())}
                onChange={(e) => setDraftTo(e.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
          </div>
          {invalid ? (
            <div className="mt-2 text-[11px] text-destructive">
              {t('period_invalid_range', lang)}
            </div>
          ) : null}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={apply}
              disabled={!draftFrom || !draftTo || !!invalid}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                (!draftFrom || !draftTo || invalid)
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              )}
            >
              {t('period_apply', lang)}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* KpiCard — valor + delta + descrição                                       */
/* -------------------------------------------------------------------------- */

function DeltaPill({ deltaPct, lang }) {
  // Delta = null → mostramos "—" (sem baseline ou divisão por zero); o user
  // explicitamente pediu este comportamento para evitar deltas inflacionados.
  if (deltaPct === null || deltaPct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
        <Minus className="h-3 w-3" aria-hidden="true" />
        {t('vs_previous_period', lang)}
      </span>
    );
  }
  const positive = deltaPct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive
    ? 'bg-success/10 text-success'
    : 'bg-destructive/10 text-destructive';
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold',
      color,
    )}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {positive ? '+' : ''}{formatNumber(deltaPct, lang, 1)}% {t('vs_previous_period', lang)}
    </span>
  );
}

/**
 * Resolve a descrição do KPI para um string já traduzido.
 *
 * Suporta 3 modos (preferência por esta ordem):
 *   1. ``description_key`` (+ ``description_args``): chave i18n com
 *      placeholders ``{nome}`` substituídos pelos args. Ex:
 *        key="kpi_desc_won_lost", args={won: 12, lost: 4}
 *        i18n PT="{won} ganhas · {lost} perdidas"
 *        →  "12 ganhas · 4 perdidas"
 *   2. ``description``: string fixa (legacy, deveria ser raro daqui em
 *      diante porque deixa de ser traduzível; mantido para retrocompat).
 *   3. nada → null.
 */
function resolveKpiDescription(kpi, lang) {
  if (kpi.description_key) {
    let s = t(kpi.description_key, lang);
    const args = kpi.description_args || {};
    for (const [k, v] of Object.entries(args)) {
      // Formatação numérica para os args numéricos — euro formato curto
      // (sem casas decimais) é o caso mais comum no rodapé do KPI.
      let display;
      if (typeof v === 'number') {
        display = formatNumber(v, lang);
      } else {
        display = v;
      }
      s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), display);
    }
    return s;
  }
  return kpi.description || null;
}

export function KpiCard({ kpi, lang }) {
  const Icon = ICONS[kpi.icon] || Activity;
  const tone = kpi.tone || 'default';
  const description = resolveKpiDescription(kpi, lang);
  const tooltip = kpi.tooltip_key ? t(kpi.tooltip_key, lang) : null;
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <span className="truncate">{t(kpi.label_key, lang)}</span>
              {tooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={tooltip}
                      className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground/70 outline-none transition-colors hover:text-foreground focus-visible:text-foreground"
                    >
                      <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="start"
                    className="max-w-xs text-left text-[11px] leading-relaxed normal-case tracking-normal"
                  >
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>
            <div className={cn(
              'mt-2 text-[1.7rem] font-semibold tracking-tight tnum',
              TONE_FG[tone] || 'text-foreground',
            )}>
              {formatKpiValue(kpi.value, kpi.format, lang)}
            </div>
            {description ? (
              <div className="mt-1 truncate text-xs text-muted-foreground">
                {description}
              </div>
            ) : null}
          </div>
          <div className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
            TONE_BG[tone],
          )}>
            <Icon className={cn('h-5 w-5', TONE_FG[tone])} aria-hidden="true" />
          </div>
        </div>
        <div className="mt-3">
          <DeltaPill deltaPct={kpi.delta_pct} lang={lang} />
        </div>
      </CardContent>
    </Card>
  );
}

export function KpiGrid({ kpis, lang }) {
  // 5 cards: 2/3/5 colunas conforme largura para evitar wrap feio em ecrãs
  // intermédios. 4 cards: 2/4. Heurística simples — funciona para os 4–5
  // cards que cada perfil tem.
  const cols = kpis.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4';
  return (
    <div className={cn('grid gap-4 grid-cols-2 md:grid-cols-3', cols)}>
      {kpis.map((k, i) => <KpiCard key={i} kpi={k} lang={lang} />)}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* DonutChartCard                                                             */
/* -------------------------------------------------------------------------- */

export function DonutChartCard({ title, data, lang, total = null,
                                 emptyKey = 'no_data',
                                 colorMap = null, valueLabel = null }) {
  // data: [{key, count}] ou [{key, value}] — escolhemos count como métrica
  // primária; quando vier `value`, mostramos esse.
  const items = (data || []).map((d, idx) => ({
    name: d.key,
    value: d.count != null ? d.count : (d.value || 0),
    color: colorMap?.[d.key] || CHART_PALETTE[idx % CHART_PALETTE.length],
  }));
  const sum = items.reduce((a, b) => a + (b.value || 0), 0);
  const totalDisplay = total != null
    ? formatNumber(total, lang)
    : formatNumber(sum, lang);
  const empty = sum === 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
            {t(emptyKey, lang)}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={items}
                    dataKey="value"
                    cx="50%" cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {items.map((it, idx) => (
                      <Cell key={idx} fill={it.color} />
                    ))}
                  </Pie>
                  <RTooltip
                    formatter={(v, n) => [formatNumber(v, lang), translateLabel(n, lang)]}
                    contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold tnum">{totalDisplay}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t(valueLabel || 'total', lang)}
                </div>
              </div>
            </div>
            <ul className="flex flex-col justify-center gap-1.5 text-sm">
              {items.map((it) => {
                const pct = sum > 0 ? (it.value / sum * 100) : 0;
                return (
                  <li key={it.name} className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: it.color }}
                      />
                      <span className="truncate">{t(it.name, lang)}</span>
                    </span>
                    <span className="tnum text-muted-foreground">
                      {formatNumber(it.value, lang)} <span className="text-xs">({formatNumber(pct, lang, 1)}%)</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* TimeSeriesCard — várias séries (created/won/lost ou entered/left)         */
/* -------------------------------------------------------------------------- */

export function TimeSeriesCard({ title, data, series, lang, kind = 'line', emptyKey = 'no_data' }) {
  // series: [{key, labelKey, color}]
  const empty = !data || data.length === 0
    || data.every((d) => series.every((s) => !d[s.key]));
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
            {t(emptyKey, lang)}
          </div>
        ) : (
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              {kind === 'bar' ? (
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                    formatter={(v, k) => [
                      formatNumber(v, lang),
                      t(series.find((s) => s.key === k)?.labelKey || k, lang),
                    ]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }}
                          formatter={(v) => t(series.find((s) => s.key === v)?.labelKey || v, lang)} />
                  {series.map((s) => (
                    <Bar key={s.key} dataKey={s.key} fill={s.color} radius={[4, 4, 0, 0]} />
                  ))}
                </BarChart>
              ) : (
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                    formatter={(v, k) => [
                      formatNumber(v, lang),
                      t(series.find((s) => s.key === k)?.labelKey || k, lang),
                    ]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }}
                          formatter={(v) => t(series.find((s) => s.key === v)?.labelKey || v, lang)} />
                  {series.map((s) => (
                    <Line
                      key={s.key} type="monotone" dataKey={s.key}
                      stroke={s.color} strokeWidth={2} dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* HorizontalBarCard — usado para PnL por comercial (stacked horizontal)     */
/* -------------------------------------------------------------------------- */

export function StackedBarCard({ title, data, lang, emptyKey = 'no_data' }) {
  // data: [{name, OK, SUBPRECIFICADO, SOBREPRECIFICADO, CRITICO, total}]
  const empty = !data || data.length === 0;
  // Recharts não tem horizontal stacked com BarChart layout='vertical' fora da caixa
  // de forma elegante para o nosso caso, então usamos stacked vertical com tooltip.
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
            {t(emptyKey, lang)}
          </div>
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ left: 12, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }}
                       stroke="hsl(var(--muted-foreground))" width={120} />
                <RTooltip contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                          formatter={(v, k) => [formatNumber(v, lang), t(`pnl_verdict_${k.toLowerCase()}`, lang)]} />
                <Legend wrapperStyle={{ fontSize: 11 }}
                        formatter={(v) => t(`pnl_verdict_${v.toLowerCase()}`, lang)} />
                <Bar dataKey="OK" stackId="a" fill={PNL_VERDICT_COLORS.OK} />
                <Bar dataKey="SOBREPRECIFICADO" stackId="a" fill={PNL_VERDICT_COLORS.SOBREPRECIFICADO} />
                <Bar dataKey="SUBPRECIFICADO" stackId="a" fill={PNL_VERDICT_COLORS.SUBPRECIFICADO} />
                <Bar dataKey="CRITICO" stackId="a" fill={PNL_VERDICT_COLORS.CRITICO} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* HorizontalBarCard — ranking de itens com % e count                         */
/*                                                                            */
/* Pensado para indicadores tipo "que máquinas são mais usadas em RFQs        */
/* ganhas?" ou "que operações de acabamento aparecem mais nas vendas?".      */
/* Cada linha é uma barra horizontal full-width com label à esquerda e        */
/* count + % à direita. Mais legível que um BarChart vertical para ranks.   */
/*                                                                            */
/* Props:                                                                     */
/*   - data: [{key, count, percent}] ordenado descendentemente pelo backend  */
/*   - valueKey: 'count' (default) ou 'percent' — controla o que dimensiona  */
/*     o comprimento da barra. 'count' é o default porque para máquinas      */
/*     (cada RFQ usa uma só) faz sentido; 'percent' é o default para         */
/*     finishing (cada RFQ pode usar várias e a soma > 100%).                 */
/*   - showPercent: bool — se true, a barra é dimensionada pela percent      */
/*     em vez do count. Mostramos sempre count + % no rodapé da barra.        */
/*   - palette: opcional, array de cores (default usa CHART_PALETTE).        */
/*   - maxItems: trunca top-N (default 8) para o card não ficar muito grande.*/
/* -------------------------------------------------------------------------- */

export function HorizontalBarCard({
  title, data, lang, emptyKey = 'no_data',
  showPercent = false, maxItems = 8, palette = CHART_PALETTE,
}) {
  const items = (data || []).slice(0, maxItems);
  const empty = items.length === 0;
  // Normalização: a maior barra ocupa 100% da largura visual.
  const maxValue = items.reduce(
    (m, it) => Math.max(m, showPercent ? (it.percent || 0) : (it.count || 0)),
    0,
  ) || 1;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
            {t(emptyKey, lang)}
          </div>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {items.map((it, idx) => {
              const value = showPercent ? (it.percent || 0) : (it.count || 0);
              const widthPct = (value / maxValue) * 100;
              const color = palette[idx % palette.length];
              const label = translateLabel(it.key, lang);
              return (
                <li key={`${it.key}-${idx}`} className="text-sm">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate font-medium" title={label}>{label}</span>
                    <span className="shrink-0 tnum text-xs text-muted-foreground">
                      {formatNumber(it.count || 0, lang)}{' '}
                      <span className="text-foreground/60">
                        ({formatNumber(it.percent || 0, lang, 1)}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${widthPct}%`, backgroundColor: color }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Tabela operacional (link para RFQ + badge de status)                       */
/* -------------------------------------------------------------------------- */

export function OperationalTable({ title, items, lang, emptyKey = 'no_data',
                                   columns, rowLink = (row) => `/rfqs/${row.id}` }) {
  const empty = !items || items.length === 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {empty ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">
            {t(emptyKey, lang)}
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {columns.map((c) => (
                    <th key={c.key}
                        className={cn(
                          'px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
                          c.align === 'right' && 'text-right',
                        )}
                    >
                      {t(c.labelKey, lang)}
                    </th>
                  ))}
                  <th className="w-8" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {items.map((row, i) => (
                  <tr key={row.id || i} className="border-b border-border/50 last:border-0">
                    {columns.map((c) => (
                      <td key={c.key}
                          className={cn(
                            'px-4 py-2 align-middle',
                            c.align === 'right' && 'text-right tnum',
                          )}
                      >
                        {c.render ? c.render(row, lang) : (row[c.key] ?? '—')}
                      </td>
                    ))}
                    <td className="px-2 py-2 text-right">
                      <Link to={rowLink(row)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="open">
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers para renderers de tabelas                                          */
/* -------------------------------------------------------------------------- */

export function StatusBadge({ status, lang }) {
  return (
    <Badge variant={statusBadgeVariant(status)}>{t(status, lang)}</Badge>
  );
}

export function VerdictBadge({ verdict, lang }) {
  // Mapa rápido para variantes de cor — alinhado com PNL_VERDICT_COLORS.
  const variant = verdict === 'CRITICO' ? 'destructive'
    : verdict === 'SUBPRECIFICADO' ? 'warning'
    : verdict === 'SOBREPRECIFICADO' ? 'info'
    : verdict === 'OK' ? 'success'
    : 'outline';
  return <Badge variant={variant}>{t(`pnl_verdict_${(verdict || 'unknown').toLowerCase()}`, lang)}</Badge>;
}

/* -------------------------------------------------------------------------- */
/* Layout wrapper                                                             */
/* -------------------------------------------------------------------------- */

export function DashboardShell({ user, lang, period, onPeriodChange, children }) {
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greeting_morning', lang);
    if (hour < 19) return t('greeting_afternoon', lang);
    return t('greeting_evening', lang);
  }, [lang]);
  const name = user?.full_name?.split(' ')?.[0] || user?.username || '';
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t('dashboard', lang)}
          </div>
          <h1 className="page-title mt-1">
            {greeting}{name ? `, ${name}` : ''}
          </h1>
        </div>
        <PeriodPicker value={period} onChange={onPeriodChange} lang={lang} />
      </div>
      {children}
    </div>
  );
}
